<script setup lang="ts">
import { useSmarticoUserInfos } from '~/stores/smartico/userInfos';

const { userCurrency } = useUserData();
const { isCasinoGamePage } = useCasino();
const { isMobile } = useLayout();
const { userCanOnlyWithdraw } = useUserLimits();
const { openDepositModal, openWithdrawModal } = usePayments();
const { isAuthenticated } = storeToRefs(useAuthStore());

const { isNewAuthButtonsEnabled, isNewDepositButtonPillEnabled } =
  useFeatureFlagsNew();

// Smartico notifications
const { isNotificationActived } = storeToRefs(useSmarticoConfigs());
const { unReadMessages } = storeToRefs(useSmarticoMessages());
const { runAction } = useSmarticoInit();

const notificationToPage = ref(true);

const unReadMessagesCount = computed(() => {
  return unReadMessages.value >= 9 ? '9+' : unReadMessages.value;
});

const hasUnreadMessages = computed(() => unReadMessages.value > 0);

function openNotifications() {
  runAction('dp:inbox');
}

function handleSmarticoInboxClick() {
  if (typeof window !== 'undefined' && (window as any)._smartico) {
    (window as any)._smartico.dp('dp:inbox');
  } else {
    console.error('Smartico script not loaded or _smartico object not found.');
  }
}

const buttonsAddClass = computed(() => {
  return isNewAuthButtonsEnabled.value ? 'new' : '';
});

const { walletIsLoading, walletId, walletRealFormatted, updateWallet } =
  useWallet();

const isAmountPendingState = computed<boolean>(() => {
  return !walletId.value || walletIsLoading.value;
});

const hideWalletBalance = computed(() => {
  return !isMobile.value && isCasinoGamePage.value;
});

const showButtonDepositPill = computed(() => {
  if (userCurrency.value === 'BRL' || userCurrency.value === 'MXN') {
    return isNewAuthButtonsEnabled.value && isNewDepositButtonPillEnabled.value;
  }

  return false;
});

// Dropdown state
const isDropdownOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

// Route check for VIP Store
const route = useRoute();
const isVipStorePage = computed(() => {
  const routePath = route.path || '';
  const routeName = route.name ? String(route.name) : '';

  return routePath.includes('/vip/store') || routeName.includes('vip-store');
});

// Smartico currencies for VIP Store
const { userInfosProfile } = storeToRefs(useSmarticoUserInfos());

const vipCurrencies = computed(() => [
  {
    name: 'COINS',
    value:
      userInfosProfile?.value?.ach_points_balance?.toLocaleString('pt-BR') ??
      '---',
    icon: 'currency-header/coin',
  },
  {
    name: 'DIAMONDS',
    value:
      userInfosProfile?.value?.ach_diamonds_balance?.toLocaleString('pt-BR') ??
      '---',
    icon: 'currency-header/diamond',
  },
  {
    name: 'GEMS',
    value:
      userInfosProfile?.value?.ach_gems_balance?.toLocaleString('pt-BR') ??
      '---',
    icon: 'currency-header/gem',
  },
]);

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function closeDropdown() {
  isDropdownOpen.value = false;
}

function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
}

onMounted(() => {
  setTimeout(() => {
    if (isAuthenticated.value && isAmountPendingState.value === true) {
      updateWallet();
    }
  }, 3000);

  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    class="flex flex-wrap gap-2 select-none justify-end lg:justify-start items-center"
  >
    <div class="flex gap-2 order-1 lg:order-2 items-center">
      <button
        class="flex w-[30px] h-[30px] justify-center items-center gap-2.5 border rounded-[9px] border-solid relative"
        :class="
          hasUnreadMessages
            ? 'border-[#FF3400] [background:linear-gradient(180deg,rgba(255,52,0,0.50)_0%,rgba(255,112,74,0.50)_100%)]'
            : 'border-white/40'
        "
        :draggable="false"
        @click="
          notificationToPage ? handleSmarticoInboxClick() : openNotifications()
        "
      >
        <div
          v-if="hasUnreadMessages"
          class="w-3.5 h-3.5 left-[20px] top-[-8px] absolute bg-[radial-gradient(ellipse_138.00%_168.30%_at_36.76%_-16.67%,_#FF704A_0%,_#FF3400_100%)] rounded-lg flex justify-center items-center gap-0.5"
        >
          <div
            v-if="isNotificationActived"
            class="text-center justify-center text-white text-[8px] font-bold leading-[9px]"
          >
            {{ unReadMessagesCount }}
          </div>
        </div>
        <NuxtIcon
          class="w-4 h-4 text-white"
          name="user/notification-letter"
        />
      </button>

      <NuxtLink
        v-if="!isMobile && isAuthenticated"
        class="deposit-btn-icon flex w-[30px] h-[30px] justify-center items-center border rounded-[9px] border-solid border-white/40 flex-shrink-0"
        to="/user/wallet"
      >
        <NuxtIcon
          class="w-4 h-4 text-white"
          filled
          name="currency-header/deposit-btn"
        />
      </NuxtLink>

      <template v-if="userCanOnlyWithdraw">
        <button
          class="btn btn-cta btn-deposit btn-withdraw"
          :class="[buttonsAddClass, appNameLower()]"
          @click="openWithdrawModal()"
        >
          {{ $t('account.wallet.withdraw') }}
        </button>
      </template>

      <button
        v-else
        class="flex h-[30px] lg:h-[40px] items-center gap-2.5 [background:radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%)] px-2.5 py-[7px] rounded-[9px] text-white text-center text-xs font-semibold leading-[10px] relative"
        :class="[buttonsAddClass, appNameLower()]"
        @click="openDepositModal()"
      >
        <div
          v-if="showButtonDepositPill"
          class="depositButtonPill"
        >
          <template v-if="userCurrency === 'MXN'">
            <span>SPEI</span>
          </template>

          <template v-else>
            <NuxtIcon
              class="text-[10px]"
              :filled="false"
              name="payments/pix_icon"
            />
            <span>PIX</span>
          </template>
        </div>
        <span
          style="
            color: var(--White, #fff);
            text-align: center;
            font-size: 13px;
            font-style: normal;
            font-weight: 500;
            line-height: 10px;
          "
          >{{ $t('header.deposit') }}</span
        >
      </button>

      <UserHeaderMenu :buttons-add-class="buttonsAddClass" />
    </div>

    <div
      ref="dropdownRef"
      class="wallet-dropdown-wrapper relative order-2 gap-2 lg:order-1 basis-full lg:basis-auto pt-1 pb-1 lg:pb-0 lg:pt-0 flex items-center justify-end"
    >
      <div
        class="btn-balance rounded-lg cursor-pointer"
        :class="{
          active: isDropdownOpen,
          'vip-store-expanded': isVipStorePage && isAuthenticated,
        }"
        @click="toggleDropdown"
      >
        <button
          aria-label="Update Wallet Balance"
          class="reloadButton"
          :class="[{ animate: isAmountPendingState }]"
          @click.stop="!hideWalletBalance && updateWallet()"
        >
          <NuxtIcon
            class="text-[10px] lg:text-[13px]"
            name="refresh"
          />
        </button>

        <!-- VIP Store: Show coins, diamonds, gems -->
        <template v-if="isVipStorePage && isAuthenticated">
          <div class="vip-currencies-row">
            <div
              v-for="currency in vipCurrencies"
              :key="currency.name"
              class="vip-currency-item"
            >
              <NuxtIcon
                class="text-[14px] lg:text-[18px]"
                filled
                :name="currency.icon"
              />
              <div class="vip-currency-info">
                <span class="vip-currency-value">{{ currency.value }}</span>
                <span class="vip-currency-label">{{ currency.name }}</span>
              </div>
            </div>
          </div>
          <NuxtIcon
            class="text-[13px] lg:text-[16px] lg:pt-0.5"
            name="arrows/angle_down"
          />
        </template>

        <!-- Default: Show balance -->
        <template v-else>
          <div class="flex flex-col gap-0">
            <template v-if="hideWalletBalance">
              <span class="playingMsg"> (Jogando) </span>
            </template>

            <template v-else>
              <div class="flex gap-2 justify-center items-center">
                <span
                  class="realAmount pt-[1px]"
                  :class="{ 'opacity-30': isAmountPendingState }"
                >
                  {{ walletRealFormatted }}
                </span>
                <NuxtIcon
                  class="text-[13px] lg:text-[16px] lg:pt-0.5"
                  name="arrows/angle_down"
                />
              </div>
            </template>
          </div>
        </template>
      </div>

      <NuxtLink
        v-if="isMobile && isAuthenticated"
        class="deposit-btn-icon flex w-[30px] h-[28px] justify-center items-center border rounded-[9px] border-solid border-white/40 flex-shrink-0"
        to="/user/wallet"
      >
        <NuxtIcon
          class="w-4 h-4 text-white"
          filled
          name="currency-header/deposit-btn"
        />
      </NuxtLink>

      <transition name="fade">
        <UserHeaderCurrencyDropdown
          v-if="isDropdownOpen"
          class="absolute top-full mt-2 left-4 z-50"
          @close="
            isDropdownOpen = false;
            navigateTo('/user/wallet');
          "
        />
      </transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wallet-dropdown-wrapper {
  @apply w-auto max-w-[220px] lg:max-w-[260px];
}

.btn-balance {
  display: flex;
  min-width: 117px;
  max-width: 220px;
  height: 28px;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-radius: 9px;
  background-color: #ffffff;
  transition: background-color 0.2s ease;

  &.active {
    background-color: #e5e5e5;
  }

  @media (min-width: 1024px) {
    min-width: 130px;
    max-width: 260px;
    height: 30px;
  }

  .realAmount {
    text-align: center;
    font-size: 12px;
    font-weight: 800;
    line-height: 12px;
    letter-spacing: -0.24px;

    @media (min-width: 1024px) {
      font-size: 13px;
      line-height: 14px;
      letter-spacing: -0.26px;
    }
  }

  .bonusAmount {
    @apply text-xs leading-4;
  }

  .playingMsg {
    @apply text-black text-[.75rem] font-bold;
  }

  .reloadButton {
    @apply text-[.9em] md:text-[1em];
    svg {
      @apply text-[1em];
    }
  }

  &.new {
    @apply rounded-3xl max-h-[28px] lg:max-h-[34px] -mr-1;
    .realAmount {
      @apply text-[.72rem] lg:text-[.8rem];
    }
    .bonusAmount {
      @apply text-[.65rem] lg:text-[.72rem] -mt-1;
    }
  }
}

.btn-deposit {
  @apply text-sm font-bold relative;
  @apply px-3 lg:px-4;

  &:after {
    content: '';
    @apply absolute top-0 left-0 w-full h-full opacity-60;
    box-shadow: 0 0 10px var(--tw-shadow-color);
  }

  &.new {
    @apply rounded-3xl h-[28px] lg:h-[34px] text-[13px] md:text-[15px];
    &:after {
      @apply rounded-3xl;
    }
  }

  &.btn-withdraw {
    @apply bg-white text-bg-primary font-semibold shadow-none;
    &:after {
      @apply hidden;
    }
  }
}

.depositButtonPill {
  @apply flex w-[34px] h-3.5 justify-center items-center gap-0.5 absolute [background:linear-gradient(114deg,#FFF_34.75%,#E5E5E5_100%)] rounded-[9px] right-[9px] -top-2 text-[#000717] text-center text-[8px] font-bold leading-[11px];
}

.animate {
  animation: rotate 600ms infinite linear;
}

@keyframes rotate {
  to {
    transform: rotate(1turn);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.deposit-btn-icon {
  cursor: pointer;
  line-height: 0;
}

// VIP Store expanded styles
.btn-balance.vip-store-expanded {
  min-width: auto;
  max-width: none;
  gap: 8px;
  padding: 8px 8px;

  @media (min-width: 1024px) {
    padding: 8px 12px;
  }
}

.vip-currencies-row {
  display: flex;
  align-items: center;
  gap: 10px;

  @media (min-width: 1024px) {
    gap: 14px;
  }
}

.vip-currency-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.vip-currency-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.vip-currency-value {
  font-size: 10px;
  font-weight: 800;
  line-height: 1.2;
  color: #000;

  @media (min-width: 1024px) {
    font-size: 12px;
  }
}

.vip-currency-label {
  font-size: 7px;
  font-weight: 600;
  color: #9ca3af;

  @media (min-width: 1024px) {
    font-size: 8px;
  }
}
</style>
